import React, { useState, useRef, useCallback, useEffect } from 'react';
import { BookOpen, Upload, FileText, CheckCircle, XCircle, AlertCircle, Loader2, ChevronDown, ChevronRight, Clock, Hash, ExternalLink, X, Pause } from 'lucide-react';
import { useTheme } from '@framework/contexts/ThemeContext';
import { parseApiDate } from '@framework/utils/dateUtils';
import { RecipeDetailModal } from './RecipesPage';

const STATUS_BADGE = {
  processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

let queueIdCounter = 0;

const ImportPage = () => {
  const { apiService: api } = useTheme();
  const [queue, setQueue] = useState([]);          // [{ id, file, status, result, error }]
  const [currentIndex, setCurrentIndex] = useState(-1); // -1 = not started
  const [pageStart, setPageStart] = useState('');
  const [pageEnd, setPageEnd] = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const pollStatusRef = useRef(null);
  const lastProgressRef = useRef(-1);
  const staleCountRef = useRef(0);
  const queueRef = useRef(queue);
  queueRef.current = queue;

  // Import history state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const [retrySuccess, setRetrySuccess] = useState(null);

  // Auto-refresh history while any import is processing
  const historyPollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const data = await api.getBookImports();
      setHistory(data);

      // Resume progress bar for NEW uploads (started from this page session)
      const active = data.find(imp => imp.status === 'processing');
      if (active?.job_id && !pollRef.current && importing) {
        setStatusMessage('Resuming import progress...');
        lastProgressRef.current = -1;
        staleCountRef.current = 0;
        pollRef.current = setInterval(() => pollStatusRef.current(active.job_id), 2000);
      }

      // Auto-refresh history while any import is processing (for retries)
      if (active && !historyPollRef.current) {
        historyPollRef.current = setInterval(async () => {
          try {
            const refreshed = await api.getBookImports();
            setHistory(refreshed);
            const stillActive = refreshed.find(imp => imp.status === 'processing');
            if (!stillActive && historyPollRef.current) {
              clearInterval(historyPollRef.current);
              historyPollRef.current = null;
            }
          } catch (err) {
            console.error('History refresh error:', err);
          }
        }, 5000);
      }
    } catch (err) {
      console.error('Failed to load import history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [api, importing]);

  useEffect(() => {
    loadHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (historyPollRef.current) clearInterval(historyPollRef.current);
    };
  }, [loadHistory]);

  const toggleExpand = async (importId) => {
    if (expandedId === importId) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(importId);
    setExpandedDetail(null);
    setDetailLoading(true);
    try {
      const detail = await api.getBookImportDetail(importId);
      setExpandedDetail(detail);
    } catch (err) {
      console.error('Failed to load import detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const VALID_EXTENSIONS = ['.pdf', '.djvu', '.djv'];
  const [skippedDuplicates, setSkippedDuplicates] = useState([]);

  const addFiles = useCallback((fileList) => {
    const validFiles = [];
    for (const f of fileList) {
      const name = f.name.toLowerCase();
      if (VALID_EXTENSIONS.some(ext => name.endsWith(ext))) {
        validFiles.push(f);
      }
    }
    if (validFiles.length === 0) return;

    // Check against import history — skip files already imported (completed or processing)
    const historyNames = new Set(
      history
        .filter(h => h.status === 'completed' || h.status === 'processing')
        .map(h => h.filename)
    );

    const dupes = validFiles.filter(f => historyNames.has(f.name));
    const fresh = validFiles.filter(f => !historyNames.has(f.name));

    if (dupes.length > 0) {
      setSkippedDuplicates(dupes.map(f => f.name));
    }

    if (fresh.length === 0) return;

    setQueue(prev => {
      const existingNames = new Set(prev.map(q => q.file.name));
      const newItems = fresh
        .filter(f => !existingNames.has(f.name))
        .map(f => ({ id: ++queueIdCounter, file: f, status: 'pending', result: null, error: null }));
      return [...prev, ...newItems];
    });
  }, [history]);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleFileSelect = useCallback((e) => {
    addFiles(Array.from(e.target.files));
    e.target.value = ''; // reset so same file can be re-selected
  }, [addFiles]);

  const removeFromQueue = useCallback((id) => {
    setQueue(prev => prev.filter(q => q.id !== id));
  }, []);

  const processFile = useCallback(async (index) => {
    const currentQueue = queueRef.current;
    if (index >= currentQueue.length) {
      // All done
      setImporting(false);
      setCurrentIndex(-1);
      loadHistory();
      return;
    }

    setCurrentIndex(index);
    setProgress(0);
    setStatusMessage('Uploading file...');
    lastProgressRef.current = -1;
    staleCountRef.current = 0;

    // Mark current item as processing
    setQueue(prev => prev.map((q, i) => i === index ? { ...q, status: 'processing' } : q));

    try {
      const item = currentQueue[index];
      const response = await api.importBook(
        item.file,
        pageStart ? parseInt(pageStart) : null,
        pageEnd ? parseInt(pageEnd) : null
      );

      const jobId = response.job_id;
      setStatusMessage('Processing...');

      // Poll until complete, then advance to next
      pollRef.current = setInterval(async () => {
        try {
          const status = await api.getJobStatus(jobId);
          const currentProgress = status.progress || 0;
          setProgress(currentProgress);
          setStatusMessage(status.message || '');

          if (status.status === 'completed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setQueue(prev => prev.map((q, i) => i === index ? { ...q, status: 'completed', result: status.result } : q));
            processFile(index + 1);
          } else if (status.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setQueue(prev => prev.map((q, i) => i === index ? { ...q, status: 'failed', error: status.message || 'Import failed' } : q));
            processFile(index + 1);
          } else {
            // Staleness detection
            if (currentProgress === lastProgressRef.current) {
              staleCountRef.current += 1;
              if (staleCountRef.current >= 6) {
                clearInterval(pollRef.current);
                pollRef.current = null;
                setQueue(prev => prev.map((q, i) => i === index
                  ? { ...q, status: 'failed', error: 'Import appears stalled — the worker may have restarted.' }
                  : q));
                processFile(index + 1);
              }
            } else {
              staleCountRef.current = 0;
              lastProgressRef.current = currentProgress;
            }
          }
        } catch (err) {
          console.error('Poll error:', err);
        }
      }, 2000);
    } catch (err) {
      setQueue(prev => prev.map((q, i) => i === index ? { ...q, status: 'failed', error: err.message || 'Failed to start import' } : q));
      processFile(index + 1);
    }
  }, [api, pageStart, pageEnd, loadHistory]);

  // Keep pollStatusRef updated for history resume
  const pollStatus = useCallback(async (jobId) => {
    try {
      const status = await api.getJobStatus(jobId);
      const currentProgress = status.progress || 0;
      setProgress(currentProgress);
      setStatusMessage(status.message || '');

      if (status.status === 'completed' || status.status === 'failed') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (!queueRef.current.some(q => q.status === 'processing')) {
          setImporting(false);
        }
        loadHistory();
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  }, [api, loadHistory]);
  pollStatusRef.current = pollStatus;

  const handleImport = async () => {
    const pendingItems = queue.filter(q => q.status === 'pending');
    if (pendingItems.length === 0) return;

    setImporting(true);
    // Mark all pending as waiting
    setQueue(prev => prev.map(q => q.status === 'pending' ? { ...q, status: 'waiting' } : q));
    // Find the first waiting item's actual index
    const firstWaitingIndex = queue.findIndex(q => q.status === 'pending');
    processFile(firstWaitingIndex);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '?';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (isoStr) => {
    const d = parseApiDate(isoStr);
    if (!d) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  // Derived state
  const pendingCount = queue.filter(q => q.status === 'pending').length;
  const allDone = importing === false && queue.length > 0 && queue.every(q => q.status === 'completed' || q.status === 'failed');
  const currentItem = currentIndex >= 0 && currentIndex < queue.length ? queue[currentIndex] : null;
  const totalToProcess = queue.filter(q => q.status !== 'pending').length || queue.length;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Import Recipe Book</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Upload PDF or DJVU cookbooks to extract recipes using AI vision analysis.
        </p>
      </div>

      {/* Upload Section — always visible */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        {/* Drop Zone */}
        <div
          onDrop={handleFileDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            border-gray-300 dark:border-gray-600 hover:border-orange-400 dark:hover:border-orange-500"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.djvu,.djv"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <div className="space-y-2">
            <Upload className="w-12 h-12 text-gray-400 mx-auto" />
            <p className="text-gray-600 dark:text-gray-300">
              {importing
                ? 'Drop more files to add to queue'
                : 'Drop PDF or DJVU cookbooks here or click to select'}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Multiple files supported. PDF and DJVU up to 100MB each.
            </p>
          </div>
        </div>

        {/* Queue List */}
        {queue.length > 0 && (
          <div className="mt-4 space-y-2">
            {queue.map((item, idx) => (
              <div
                key={item.id}
                className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/50"
              >
                {/* Status icon */}
                {item.status === 'pending' && (
                  <FileText className="w-4 h-4 text-orange-500 flex-shrink-0" />
                )}
                {item.status === 'waiting' && (
                  <Pause className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
                {item.status === 'processing' && (
                  <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
                )}
                {item.status === 'completed' && (
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                )}
                {item.status === 'failed' && (
                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                )}

                {/* File info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {item.file.name}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                      {formatFileSize(item.file.size)}
                    </span>
                  </div>
                  {/* Status text */}
                  {item.status === 'waiting' && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">Waiting...</p>
                  )}
                  {item.status === 'processing' && (
                    <p className="text-xs text-blue-600 dark:text-blue-400">{statusMessage || 'Processing...'}</p>
                  )}
                  {item.status === 'completed' && item.result && (
                    <p className="text-xs text-green-600 dark:text-green-400">
                      {item.result.recipes_found} recipe{item.result.recipes_found !== 1 ? 's' : ''} imported
                    </p>
                  )}
                  {item.status === 'failed' && (
                    <p className="text-xs text-red-600 dark:text-red-400">{item.error}</p>
                  )}
                </div>

                {/* Remove button — only for pending items */}
                {item.status === 'pending' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFromQueue(item.id); }}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Duplicate warning */}
        {skippedDuplicates.length > 0 && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Skipped {skippedDuplicates.length === 1 ? 'duplicate' : `${skippedDuplicates.length} duplicates`} (already imported):
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 truncate">
                {skippedDuplicates.join(', ')}
              </p>
            </div>
            <button onClick={() => setSkippedDuplicates([])} className="text-amber-400 hover:text-amber-600 flex-shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Page Range */}
        {!allDone && (
          <div className="mt-4 flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Page (optional)
              </label>
              <input
                type="number"
                min="1"
                value={pageStart}
                onChange={(e) => setPageStart(e.target.value)}
                placeholder="1"
                disabled={importing}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm disabled:opacity-50"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Page (optional)
              </label>
              <input
                type="number"
                min="1"
                value={pageEnd}
                onChange={(e) => setPageEnd(e.target.value)}
                placeholder="Last"
                disabled={importing}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm disabled:opacity-50"
              />
            </div>
          </div>
        )}

        {/* Import Button */}
        {!importing && !allDone && (
          <button
            onClick={handleImport}
            disabled={pendingCount === 0}
            className={`mt-4 w-full py-3 rounded-lg font-medium text-white transition-colors
              ${pendingCount > 0
                ? 'bg-orange-500 hover:bg-orange-600'
                : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'}`}
          >
            <BookOpen className="w-5 h-5 inline mr-2 -mt-0.5" />
            {pendingCount > 1 ? `Import ${pendingCount} Books` : 'Import Recipes'}
          </button>
        )}

        {/* Import More — after all done */}
        {allDone && (
          <button
            onClick={() => { setQueue([]); setCurrentIndex(-1); setPageStart(''); setPageEnd(''); setSkippedDuplicates([]); }}
            className="mt-4 w-full py-3 rounded-lg font-medium text-orange-600 dark:text-orange-400 border border-orange-300 dark:border-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
          >
            Import More Books
          </button>
        )}
      </div>

      {/* Progress Section */}
      {importing && currentItem && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
            <span className="font-medium text-gray-900 dark:text-white">
              Importing {currentItem.file.name}
              {totalToProcess > 1 && (
                <span className="text-gray-400 dark:text-gray-500 font-normal ml-2">
                  ({currentIndex + 1} of {totalToProcess})
                </span>
              )}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-3">
            <div
              className="bg-orange-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${Math.max(progress, 2)}%` }}
            />
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300">{statusMessage}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            This may take several minutes for large books. Each page is analyzed individually.
          </p>
        </div>
      )}

      {/* Batch Results Summary — after all done */}
      {allDone && (
        <div className="space-y-4">
          {/* Overall summary */}
          {(() => {
            const completed = queue.filter(q => q.status === 'completed');
            const failed = queue.filter(q => q.status === 'failed');
            const totalRecipes = completed.reduce((sum, q) => sum + (q.result?.recipes_found || 0), 0);
            return (
              <div className={`${failed.length === queue.length
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              } border rounded-lg p-4 flex items-start gap-3`}>
                {failed.length === queue.length
                  ? <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  : <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                }
                <div>
                  <p className={`font-medium ${failed.length === queue.length
                    ? 'text-red-800 dark:text-red-200'
                    : 'text-green-800 dark:text-green-200'
                  }`}>
                    {queue.length === 1 ? 'Import Complete' : 'All Imports Complete'}
                  </p>
                  <p className={`text-sm mt-1 ${failed.length === queue.length
                    ? 'text-red-600 dark:text-red-300'
                    : 'text-green-600 dark:text-green-300'
                  }`}>
                    {completed.length > 0 && `${totalRecipes} recipe${totalRecipes !== 1 ? 's' : ''} from ${completed.length} book${completed.length !== 1 ? 's' : ''}`}
                    {completed.length > 0 && failed.length > 0 && '. '}
                    {failed.length > 0 && `${failed.length} book${failed.length !== 1 ? 's' : ''} failed.`}
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Per-file results — only for multi-file batches with completed items */}
          {queue.length > 1 && queue.some(q => q.status === 'completed' && q.result?.recipes?.length > 0) && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">Results by Book</h3>
              <div className="space-y-3">
                {queue.map((item) => (
                  <div key={item.id}>
                    <div className="flex items-center gap-2 mb-1">
                      {item.status === 'completed'
                        ? <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        : <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                      }
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{item.file.name}</span>
                      {item.status === 'completed' && item.result && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                          {item.result.recipes_found} recipe{item.result.recipes_found !== 1 ? 's' : ''}
                        </span>
                      )}
                      {item.status === 'failed' && (
                        <span className="text-xs text-red-500 dark:text-red-400 ml-auto">{item.error}</span>
                      )}
                    </div>
                    {item.result?.recipes?.length > 0 && (
                      <div className="ml-5 space-y-0.5">
                        {item.result.recipes.map((recipe, idx) => (
                          <div key={idx} className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-2">
                            <span className="truncate">{recipe.name}</span>
                            {recipe.page && <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">p.{recipe.page}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Retry Success */}
      {retrySuccess && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-green-800 dark:text-green-200">Retry Started</p>
            <p className="text-sm text-green-600 dark:text-green-300 mt-1">{retrySuccess}</p>
          </div>
          <button onClick={() => setRetrySuccess(null)} className="text-green-400 hover:text-green-600">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ============================================================ */}
      {/* Import History */}
      {/* ============================================================ */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-400" />
            Import History
          </h2>
        </div>

        {historyLoading ? (
          <div className="p-6 text-center">
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin mx-auto" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading history...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
            No imports yet. Upload a book above to get started.
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {history.map((imp) => (
              <div key={imp.id}>
                {/* Row */}
                <button
                  onClick={() => toggleExpand(imp.id)}
                  className="w-full px-6 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                >
                  {expandedId === imp.id
                    ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  }
                  <FileText className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {imp.filename}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                    {imp.page_start || imp.page_end
                      ? `p.${imp.page_start || 1}-${imp.page_end || imp.total_pages || '?'}`
                      : imp.total_pages ? `${imp.total_pages} pages` : ''
                    }
                  </span>
                  <span className="ml-auto flex items-center gap-3 flex-shrink-0">
                    {imp.status === 'processing' && (
                      <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {imp.recipes_found > 0 ? `${imp.recipes_found} recipes so far...` : 'Processing...'}
                      </span>
                    )}
                    {imp.status === 'completed' && imp.recipes_found > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Hash className="w-3 h-3" />{imp.recipes_found} recipe{imp.recipes_found !== 1 ? 's' : ''}
                      </span>
                    )}
                    {imp.errors_count > 0 && (
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        {imp.errors_count} error{imp.errors_count !== 1 ? 's' : ''}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[imp.status] || 'bg-gray-100 text-gray-600'}`}>
                      {imp.status}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 w-28 text-right">
                      {formatDate(imp.created_at)}
                    </span>
                  </span>
                </button>

                {/* Expanded Detail */}
                {expandedId === imp.id && (
                  <div className="px-6 pb-4 pl-14">
                    {detailLoading ? (
                      <div className="py-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading recipes...
                      </div>
                    ) : expandedDetail ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          {expandedDetail.pdf_url && (
                            <a
                              href={expandedDetail.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm text-orange-600 dark:text-orange-400 hover:underline"
                            >
                              <ExternalLink className="w-3.5 h-3.5" /> View Book
                            </a>
                          )}
                          {imp.status === 'failed' && (
                            <button
                              disabled={retryingId === imp.id}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (retryingId === imp.id) return;
                                setRetryingId(imp.id);
                                try {
                                  const res = await api.retryBookImport(imp.id);
                                  setRetrySuccess(`Retrying from page ${res.resumed_from_page}...`);
                                  loadHistory();
                                } catch (err) {
                                  if (err.message?.includes('409') || err.message?.includes('already processing')) {
                                    loadHistory();
                                  }
                                } finally {
                                  setRetryingId(null);
                                }
                              }}
                              className={`inline-flex items-center gap-1.5 text-sm font-medium ${
                                retryingId === imp.id
                                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                  : 'text-orange-600 dark:text-orange-400 hover:underline'
                              }`}
                            >
                              <Loader2 className={`w-3.5 h-3.5 ${retryingId === imp.id ? 'animate-spin' : ''}`} />
                              {retryingId === imp.id ? 'Retrying...' : 'Retry Import'}
                            </button>
                          )}
                        </div>
                        {expandedDetail.recipes?.length > 0 ? (
                          <div className="space-y-1">
                            {expandedDetail.recipes.map((r) => (
                              <div
                                key={r.id}
                                onClick={() => setSelectedRecipeId(r.id)}
                                className="flex items-center gap-3 py-1.5 text-sm cursor-pointer rounded px-1 -mx-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                              >
                                {r.image_url ? (
                                  <img src={r.image_url} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                ) : (
                                  <div className="w-8 h-8 rounded bg-gray-100 dark:bg-gray-700 flex-shrink-0" />
                                )}
                                <span className="text-gray-900 dark:text-white truncate">{r.name}</span>
                                {r.page_number && (
                                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto flex-shrink-0">
                                    p.{r.page_number}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="py-3 text-sm text-gray-500 dark:text-gray-400">
                            {imp.status === 'failed' ? 'Import failed.' : 'No recipes linked to this import.'}
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {selectedRecipeId && (
        <RecipeDetailModal
          recipeId={selectedRecipeId}
          api={api}
          onClose={() => setSelectedRecipeId(null)}
          onDeleted={() => { setSelectedRecipeId(null); loadHistory(); }}
          onUpdated={() => {}}
        />
      )}
    </div>
  );
};

export default ImportPage;
